import {
	CircleAlert,
	type LucideIcon,
	SearchX,
	ServerCrash,
	WifiOff,
} from 'lucide-react';
import type { FallbackProps } from 'react-error-boundary';
import { useNavigate } from 'react-router';
import {
	AuthError,
	isAppError,
	NetworkError,
	NotFoundError,
	ServerError,
} from '@/shared/lib';
import Button from '../button';

type ErrorContent = {
	Icon: LucideIcon;
	title: string;
	description: string;
	buttonLabel: string;
	action: () => void;
};

const PageErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
	const navigate = useNavigate();

	if (!isAppError(error)) throw error;
	if (error instanceof AuthError) throw error;

	const content: ErrorContent = (() => {
		if (error instanceof NotFoundError) {
			return {
				Icon: SearchX,
				title: '찾을 수 없어요',
				description: '요청하신 페이지가 존재하지 않아요',
				buttonLabel: '돌아가기',
				action: () => navigate(-1),
			};
		}
		if (error instanceof NetworkError) {
			return {
				Icon: WifiOff,
				title: '네트워크 연결을 확인해주세요',
				description: '인터넷 연결이 끊겼어요. 연결 후 다시 시도해 주세요',
				buttonLabel: '다시 시도',
				action: resetErrorBoundary,
			};
		}
		if (error instanceof ServerError) {
			return {
				Icon: ServerCrash,
				title: '서버 오류가 발생했어요',
				description: '일시적인 오류예요. 잠시 후 다시 시도해 주세요',
				buttonLabel: '다시 시도',
				action: resetErrorBoundary,
			};
		}
		return {
			Icon: CircleAlert,
			title: '오류가 발생했어요',
			description: '알 수 없는 오류예요. 잠시 후 다시 시도해 주세요',
			buttonLabel: '다시 시도',
			action: resetErrorBoundary,
		};
	})();

	const { Icon, title, description, buttonLabel, action } = content;

	return (
		<div className="flex h-full flex-col items-center justify-center gap-6 px-5">
			<Icon className="text-mantang-gray-300" size={56} />
			<div className="flex flex-col items-center gap-2 text-center">
				<h2 className="font-semibold text-[20px] text-mantang-black">
					{title}
				</h2>
				<p className="text-base text-mantang-gray-500 leading-7">
					{description}
				</p>
			</div>
			<Button type="button" onClick={action}>
				{buttonLabel}
			</Button>
		</div>
	);
};

export default PageErrorFallback;
